import React, { useRef, useEffect, useState } from 'react';
import cn from 'classnames';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';

export interface IMapMarker extends google.maps.MarkerOptions {
	// eslint-disable-next-line no-unused-vars
	onClick?: (marker: google.maps.Marker, map: google.maps.Map) => void;
}

export interface IMapProps extends IBaseElementProps {
	center?: google.maps.LatLng | google.maps.LatLngLiteral;
	zoom?: number;
	markers?: IMapMarker[];
	// eslint-disable-next-line no-unused-vars
	onMapLoaded?: (map: google.maps.Map) => void;
}

/**
 * Map
 * @block
 * @requiresHooks
 * @icon location-alt
 */
const Map: React.FC<IMapProps> = ({
	className,
	classNames = [],
	center,
	zoom = 4,
	markers = [],
	onMapLoaded,
}) => {
	const mapRef = useRef<HTMLDivElement>(null);
	const [isLoaded, setIsLoaded] = useState(false);
	const [map, setMap] = useState<google.maps.Map | null>(null);

	useEffect(() => {
		if (!mapRef.current || !isLoaded) {
			return;
		}

	const styles = [
		{
		  "featureType": "administrative",
		  "elementType": "labels.text.fill",
		  "stylers": [
			{
			  "color": "#444444"
			}
		  ]
		},
		{
		  "featureType": "administrative.neighborhood",
		  "elementType": "labels",
		  "stylers": [
			{
			  "visibility": "off"
			}
		  ]
		},
		{
		  "featureType": "landscape",
		  "elementType": "all",
		  "stylers": [
			{
			  "color": "#f2f2f2"
			}
		  ]
		},
		{
		  "featureType": "landscape",
		  "elementType": "geometry.fill",
		  "stylers": [
			{
			  "color": "#e8f4ff"
			}
		  ]
		},
		{
		  "featureType": "poi",
		  "elementType": "all",
		  "stylers": [
			{
			  "visibility": "off"
			}
		  ]
		},
		{
		  "featureType": "road",
		  "elementType": "all",
		  "stylers": [
			{
			  "saturation": -100
			},
			{
			  "lightness": 45
			}
		  ]
		},
		{
		  "featureType": "road.highway",
		  "elementType": "all",
		  "stylers": [
			{
			  "visibility": "simplified"
			}
		  ]
		},
		{
		  "featureType": "road.highway",
		  "elementType": "geometry",
		  "stylers": [
			{
			  "visibility": "on"
			},
			{
			  "color": "#ffffff"
			}
		  ]
		},
		{
		  "featureType": "road.highway",
		  "elementType": "labels.text.fill",
		  "stylers": [
			{
			  "color": "#373737"
			}
		  ]
		},
		{
		  "featureType": "road.arterial",
		  "elementType": "geometry",
		  "stylers": [
			{
			  "color": "#ffffff"
			}
		  ]
		},
		{
		  "featureType": "road.arterial",
		  "elementType": "labels.text.fill",
		  "stylers": [
			{
			  "color": "#373737"
			}
		  ]
		},
		{
		  "featureType": "road.arterial",
		  "elementType": "labels.icon",
		  "stylers": [
			{
			  "visibility": "off"
			}
		  ]
		},
		{
		  "featureType": "road.local",
		  "elementType": "geometry",
		  "stylers": [
			{
			  "color": "#ffffff"
			}
		  ]
		},
		{
		  "featureType": "road.local",
		  "elementType": "labels.text.fill",
		  "stylers": [
			{
			  "color": "#373737"
			}
		  ]
		},
		{
		  "featureType": "transit",
		  "elementType": "all",
		  "stylers": [
			{
			  "visibility": "off"
			}
		  ]
		},
		{
		  "featureType": "water",
		  "elementType": "all",
		  "stylers": [
			{
			  "color": "#46bcec"
			},
			{
			  "visibility": "on"
			}
		  ]
		},
		{
		  "featureType": "water",
		  "elementType": "geometry",
		  "stylers": [
			{
			  "visibility": "on"
			},
			{
			  "color": "#d5ebff"
			}
		  ]
		},
		{
		  "featureType": "water",
		  "elementType": "geometry.fill",
		  "stylers": [
			{
			  "visibility": "on"
			}
		  ]
		},
		{
		  "featureType": "water",
		  "elementType": "labels.text.fill",
		  "stylers": [
			{
			  "color": "#8e9bc5"
			}
		  ]
		},
		{
		  "featureType": "water",
		  "elementType": "labels.text.stroke",
		  "stylers": [
			{
			  "visibility": "off"
			}
		  ]
		}
	  ];

		const gmap = new window.google.maps.Map(mapRef.current, {
			// @ts-ignore
			center,
			zoom,
			styles: styles,
			mapTypeControl: false,
			streetViewControl: false,
			fullscreenControl: false
		});

		setMap(gmap);

		if (onMapLoaded) {
			onMapLoaded(gmap);
		}
	}, [isLoaded]);

	useEffect(() => {
		if (!map) {
			return;
		}

		let bounds = new google.maps.LatLngBounds();
		const mapMarkers = markers.map(({ onClick, ...markerOptions }) => {
			const marker = new window.google.maps.Marker({
				...markerOptions,
				map,
				title: 'Location marker'
			});
			marker.setMap(map);
			bounds.extend( markerOptions.position );

			if (onClick) {
				marker.addListener('click', () => {
					onClick(marker, map);
				});
			}

			return marker;
		}) as google.maps.Marker[];
		map.fitBounds( bounds );

		// Listen for when map is idle to set zoom.  helpful when only one result
		const listener = google.maps.event.addListener(map, "idle", () => {
			if ( zoom !== 4 ) {
				map.setZoom( zoom );
			} else {
				const maximumZoom = 16;
				if ( map.getZoom() > maximumZoom ) {
					map.setZoom( maximumZoom );
				}
			}
			google.maps.event.removeListener(listener);
		});
		return () => {
			mapMarkers.forEach((marker) => marker.setMap(null));
		}
	}, [map, markers]);

	let googleMapsApiKey = "";

	if (typeof process !== 'undefined' && process.env && process.env.GOOGLE_MAPS_API_KEY) {
		googleMapsApiKey = process.env.GOOGLE_MAPS_API_KEY;
		// @ts-ignore
	} else if (typeof window !== 'undefined' && window.GOOGLE_MAPS_API_KEY) {
		// @ts-ignore
		googleMapsApiKey = window.GOOGLE_MAPS_API_KEY;
	}

	return (
		<>
			<Wrapper
				apiKey={googleMapsApiKey}
				region="CA"
				language="en"
				callback={(status: Status) => {
					if (status === 'SUCCESS') {
						setIsLoaded(true);
					}
				}}
			/>
			<div
				ref={mapRef}
				className={className || cn('map-container', ...classNames)}
				style={{
					width: '100%',
					height: '100%',
				}}
			/>
		</>
	);
};

export default Map;
